import {
  Component,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  Input,
  ChangeDetectorRef,
  SimpleChanges,
} from '@angular/core';
import { FeedbackModel, FeedbackType } from '@shared/model/feedback';
import { BaseComponent } from '@shared/components/base-component/base.component';
import { City } from '@shared-features/models/city';
import { ShipmentCreationTemplateTypes } from '@shared/enums/shipment-creation-template-types.enum';
import { UserType } from '@shared-features/enums/user-type.enum';
import { LocationType } from '@shared/enums/location-type.enum';
import { debounceTime, take, takeUntil } from 'rxjs/operators';
import { ShippingType } from '@shared-features/enums/shipping-type.enum';
import { LookupType } from '@shared/enums/lookup-type.enum';
import {
  FormArray,
  FormBuilder,
  FormControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { noWhitespaceValidator } from '@shared/validators/form-validators';
import { longMapURLValidator } from '@shared/validators/form-validators';
import { isPointInPolygon } from 'geolib';
import { Lookup } from '@shared-features/models/Lookup.model';
import { LocationsApiService } from '@shared-features/services/locations-api.service';
import { UsersCommonApiService } from '@shared-features/services/users-common-api.service';
import { WCCRegion } from '@features/shipment-creation-template/models/wcc-region.model';
import { WCCReceiver } from '@features/shipment-creation-template/models/wcc-receiver.model';
import { User } from '@features/users/models/user';
import { LocationModel } from '@features/live-tracking/models/location.model';
import { FunctionUtil } from '@shared/utils/function.util';
import { BusinessType } from '@shared-features/enums/business-type.enum';

@Component({
  selector: 'app-add-location',
  templateUrl: './add-location.component.html',
  styleUrls: ['./add-location.component.scss'],
})
export class AddLocationComponent extends BaseComponent implements OnInit, OnDestroy {
  @Input() item: any;
  @Input() location: LocationModel = new LocationModel();
  @Input() type: LocationType = LocationType.Customer;
  @Input() editLatLngOnly = false;
  @Input() isCityDisabled: boolean = false;
  @Input() hideActions: boolean = false;
  @Input() saveLocationToAPI: boolean = true;
  @Input() actionBtnText: string = 'Buttons.Save';
  @Input() cancelBtnText: string = 'Buttons.Cancel';
  @Output() closeModal = new EventEmitter();
  @Output() locationId = new EventEmitter();
  @Output() updatedLocation = new EventEmitter();
  @Output() reloadComponent = new EventEmitter();
  @Output() save = new EventEmitter();
  @Output() valueChanges = new EventEmitter<LocationModel>();
  @Output() formValidityChanges = new EventEmitter<boolean>();

  latitude: number = null;
  longitude: number = null;
  link = '';
  zoom = 12;
  address = '';
  city: City = new City();
  hasLocationPermission = true;
  form: UntypedFormGroup;
  originalLocation: Partial<LocationModel> = new LocationModel();

  activeCities: City[] = [];
  polygonCoordinates: any[] = [];
  selectedCity: City;

  LookupType = LookupType;
  googleMapLoaded = false;

  private geoCoder;

  ShippingType = ShippingType;
  contacts = [];
  UserType = UserType;
  LocationType = LocationType;
  selectedUsers = [];
  newContact = null;
  wccRegions: WCCRegion[] = [];
  wccReceivers: WCCReceiver[] = [];
  hasCementTemplate: boolean;
  hasOrdersTemplate: boolean;
  gateDefinitionForms: FormArray;
  filterHandler = this.filterCities.bind(this);

  protected BusinessType = BusinessType;
  protected readonly ShipmentCreationTemplateTypes = ShipmentCreationTemplateTypes;

  constructor(
    // private mapsAPILoader: MapsAPILoader,
    private formBuilder: UntypedFormBuilder,
    private changeDetector: ChangeDetectorRef,
    private locationsApiService: LocationsApiService,
    private usersCommonApiService: UsersCommonApiService,
    private builder: FormBuilder
  ) {
    super();
  }

  ngOnInit(): void {
    this.originalLocation = JSON.parse(JSON.stringify(this.location));

    this.locationsApiService.loadGoogleMapsApi().then(() => {
      this.googleMapLoaded = true;
      this.geoCoder = new google.maps.Geocoder();
      this.form?.controls?.searchAddress?.enable();

      if (this.location.id) {
        this.longitude = Number(this.location.longitude);
        this.latitude = Number(this.location.latitude);
        this.address = this.location.address;
        this.form.controls['name'].setValue(this.location.title);
      }
      else {
        this.setCurrentLocation();
      }
    });
    navigator.permissions.query({ name: 'geolocation' }).then((data) => {
      if (data.state === 'denied') {
        this.asyncFeedbackService.showFeedback(
          new FeedbackModel(
            FeedbackType.Failure,
            this.translationsList['ErrorMessages']['MapErrorLoading']
          )
        );
        this.hasLocationPermission = false;
      }
      return;
    });

    this.initForm();
  }

  ngOnChanges({ item }: SimpleChanges) {
    if (item && item.currentValue !== item.previousValue) {
      this.hasCementTemplate = (this.item?.creationTemplates || []).some(({ type }) => type === ShipmentCreationTemplateTypes.Cement);
      this.hasOrdersTemplate = (this.item?.creationTemplates || []).some(({ type }) => type === ShipmentCreationTemplateTypes.Orders);

      if (this.hasCementTemplate) {
        this.load(this.locationsApiService.getWCCRegions(this.item.id)).subscribe((res) => {
          this.wccRegions = res;
          this.form?.controls.wccRegion.setValue(
            this.wccRegions.find((region) => this.location?.wccRegion?.id === region.id)
          );
        });
      }
    }
  }

  markerDragEnd($event?: any /*MouseEvent*/, lat?: number, long?: number) {
    if (navigator.geolocation && $event !== null) {
      this.latitude = $event.latLng.lat();
      this.longitude = $event.latLng.lng();
      this.getAddress(this.latitude, this.longitude);
    } else {
      this.latitude = lat;
      this.longitude = long;
    }
  }

  onSaveClicked() {
    if (this.latitude && this.longitude && this.longitude != 0 && this.longitude != 0) {
      const { name, city, defaultUser, contacts, reference, ...formValue } =
        this.form.getRawValue();
      Object.assign(this.location, formValue, {
        title: name,
        type: this.type,
        address: this.address,
        latitude: this.latitude,
        longitude: this.longitude,
        cityId: city?.id,
        reference: reference,
      });
      const defaultUserId = defaultUser?.id;
      const usersIds = (contacts || []).map((v) => v.id);
      if (!this.saveLocationToAPI) {
        this.save.emit({ ...this.location, usersIds, defaultUserId });
        return;
      } else {
        if (this.location.id) {
          this.handleLocationEdit(this.location, usersIds, defaultUserId);
        } else {
          this.handleAddLocation(this.location, Number(this.item.id), usersIds, defaultUserId);
        }
      }
    } else
      this.asyncFeedbackService.showFailureMessage(
        this.translationsList['ErrorMessages']['EmptyLongAndLat']
      );
  }

  // private methods
  private setCurrentLocation() {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition((position) => {
        this.latitude = position.coords.latitude;
        this.longitude = position.coords.longitude;
        this.zoom = 8;
        this.getAddress(this.latitude, this.longitude);
      });
    }
  }

  handleLocationEdit(location, usersIds, defaultUserId) {
    const locationPayload = { ...this.location, usersIds, defaultUserId };

    const locationDiff = FunctionUtil.getObjectDiff(this.originalLocation, locationPayload, [
      'id',
      'shippingType',
    ]);

    this.load(this.locationsApiService.editLocation(locationDiff).pipe(take(1))).subscribe(() => {
      this.closeModal.emit();
      this.reloadComponent.emit();
      this.showSuccessMessage('SuccessMessages.LocationModifiedSuccessfully');
    });
  }

  handleAddLocation(location, eventId, usersIds, defaultUserId) {
    this.load(
      this.locationsApiService.addLocation(location, eventId, usersIds, defaultUserId).pipe(take(1))
    ).subscribe((location) => {
      this.item?.locations?.push(location);
      this.locationId.emit(location.id);
      this.closeModal.emit();
      this.reloadComponent.emit();
      this.showSuccessMessage('SuccessMessages.LocationAddedSuccessfully');
    });
  }

  private initForm() {
    const city = new Lookup();
    city.id = this.location?.city?.id || null;
    city.value = this.location?.city?.name || null;
    const gates = this.location.gates;

    this.form = this.formBuilder.group({
      formType: [this.item ? null : ShipmentCreationTemplateTypes.Basic], // frontend only to show/hide some controls
      searchAddress: [{ value: '', disabled: !this.googleMapLoaded }],
      gates: this.builder.array([[null]]), // visible when creation template = ORDER_ORIENTED & have gates
      name: [
        { value: '', disabled: this.editLatLngOnly },
        [Validators.required, noWhitespaceValidator],
      ],
      city: [
        { value: city.id ? city : '', disabled: this.editLatLngOnly || this.isCityDisabled },
        [Validators.required],
      ],
      link: [''],
      coordinates: [''],
      shippingType: [this.location?.shippingType || ShippingType.All, [Validators.required]],
      contacts: [''],
      defaultUser: [null],
      wccRegion: [null], // visible when creation template = CEMENT
      wccReceiver: [null], // visible when creation template = CEMENT
      reference: [this.location?.reference || ''],
    });

    this.gateDefinitionForms = this.form.controls.gates as FormArray;
    this.gateDefinitionForms.removeAt(0);
    if (gates) {
      gates?.forEach((gate) => {
        if (gate.truckClassify) {
          gate.temperatureControl = true;
        }
        this.gateDefinitionForms.push(new FormControl(gate, [Validators.required]));
      });
    }

    if (this.type === LocationType.Customer) {
      if (this.location?.id) {
        /**TODO: detect the problem in app-dropdown to avoid using the below method and extra arrays */
        this.getContacts(this.location.shippingType).then((users) => {
          this.setContactsControl(users);
          const _contacts = [];
          const ids = (this.location.usersLocations || []).map((l) => l.user?.id);
          ids.forEach((id) => {
            const contact = this.contacts?.find((c) => c.id === id);
            if (contact) {
              _contacts.push(contact);
            }
          });
          this.form.controls.contacts.patchValue([..._contacts]);

          this.location?.usersLocations?.some((location) => {
            if (location.isDefault) {
              this.form.controls.defaultUser.setValue(
                _contacts.filter((c) => c.id === location.user.id)[0]
              );
              return true;
            }
          });
        });
      } else {
        this.getContacts(this.form.controls.shippingType.value).then((contacts) =>
          this.setContactsControl(contacts)
        );
      }

      // Emit form value changes to parent component
      this.form.valueChanges
        .pipe(debounceTime(300), takeUntil(this.destroy$))
        .subscribe(() => this.emitLocationValue());

      // Emit form validity status to parent component
      this.form.statusChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.formValidityChanges.emit(this.form.valid));

      this.form.controls.shippingType.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          if (value && this.type === LocationType.Customer) {
            this.getContacts(this.form?.controls?.shippingType.value).then((users) => {
              this.contacts = users;
              if (this.newContact) {
                this.form.controls.contacts.patchValue([
                  (this.contacts || []).find((c) => c.id === this.newContact?.id),
                ]);
              } else if (this.contacts.length === 1) {
                this.form.controls.contacts.patchValue(this.contacts);
              } else {
                this.form.controls.contacts.setValue(null);
              }
              this.newContact = null;
            });
          }
        });

      this.form.controls.contacts.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
        if (value && value.length === 1) {
          this.form.controls.defaultUser.setValue(value[0]);
        } else {
          this.form.controls.defaultUser.setValue(null);
        }
      });
    }

    this.form.controls.wccRegion.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (value) {
        this.load(this.locationsApiService.getWCCReceivers(value.id, this.item?.id), {
          isLoadingTransparent: true,
        }).subscribe((res) => {
          this.wccReceivers = res;
          this.form?.controls.wccReceiver.setValue(
            this.wccReceivers.find((rec) => this.location?.wccReceiver?.id === rec.id)
          );
        });
      }
    });

    this.form.controls.formType.valueChanges
      .pipe(
        debounceTime(300),
        takeUntil(this.destroy$)
      ).subscribe((value: ShipmentCreationTemplateTypes) => {
        if (value === ShipmentCreationTemplateTypes.Basic) {
          this.hasCementTemplate = false;
          this.hasOrdersTemplate = false;
          this.form.get('wccRegion').setValue(null);
          this.form.get('wccReceiver').setValue(null);
          (this.form.get('gates') as FormArray).clear();
        }
        if (value === ShipmentCreationTemplateTypes.Cement) {
          this.hasCementTemplate = true;
          this.hasOrdersTemplate = false;
          (this.form.get('gates') as FormArray).clear();
        }
        if (value === ShipmentCreationTemplateTypes.Orders) {
          this.hasCementTemplate = false;
          this.hasOrdersTemplate = true;
          this.form.get('wccRegion').setValue(null);
          this.form.get('wccReceiver').setValue(null);
        }
      });

    this.onSearchMapChange();
  }


  private onSearchMapChange(): void {
    this.form.controls.searchAddress.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(place => {
        const link = this.form.controls['link'];
        link.reset('', { emitEvent: false });
        link.clearValidators();
        link.updateValueAndValidity();

        if (place?.geometry) {
          this.latitude = place?.geometry?.location?.lat();
          this.longitude = place?.geometry?.location?.lng();
          this.address = place?.formatted_address;
          this.zoom = 15;
          this.markerDragEnd(null, this.latitude, this.longitude);
        } else {
          this.latitude = null;
          this.longitude = null;
          this.address = '';
        }
        this.changeDetector.detectChanges();
      });
  }

  /**
   * Detects map location based on user input.
   *
   * By default, this method detects the map location using a copied map URL from browser's address bar.
   * In this case, the `link` form control and its implementation are used to
   * extract latitude and longitude from the URL.
   *
   * If map detection is required using copied latitude & longitude instead,
   * uncomment the `coordinates` form control implementation below and comment
   * out the `link` form control implementation. 
   * The coordinates-based flow validates and parses the lat/long values directly from the input.
   *
   * Only one detection strategy (URL or coordinates) should be active at a time.
   */
  detectMapURL(): void {
    this.form.controls.searchAddress.reset('', { emitEvent: false });
    const link = this.form.controls['link'];

    if (link.value) {
      if (!link.validator) {
        link.setValidators(longMapURLValidator);
        link.updateValueAndValidity();
      }

      if (link.valid) {
        let segments = link.value.split('@')[1].split('z')[0].split(',');
        this.latitude = Number(segments[0]);
        this.longitude = Number(segments[1]);
        this.getAddress(Number(this.latitude), Number(this.longitude));
        this.markerDragEnd(null, Number(this.latitude), Number(this.longitude));
      } else {
        this.resetCoordinatesAndAddress();
      }
    } else {
      if (link.validator) {
        link.clearValidators();
        link.updateValueAndValidity();
        this.resetCoordinatesAndAddress();
      }
    }

    // const coordinates = this.form.controls['coordinates'];
    // if (coordinates.value) {
    //   if (!coordinates.validator) {
    //     coordinates.setValidators(mapCoordinatesValidator);
    //     coordinates.updateValueAndValidity();
    //   }

    //   if (coordinates.valid) {
    //     const segments = coordinates.value.toString().trim().split(',');
    //     this.latitude = Number(segments[0]);
    //     this.longitude = Number(segments[1]);
    //     this.getAddress(Number(this.latitude), Number(this.longitude));
    //     this.markerDragEnd(null, Number(this.latitude), Number(this.longitude));
    //   } else {
    //     this.resetCoordinatesAndAddress();
    //   }
    // } else {
    //   if (coordinates.validator) {
    //     coordinates.clearValidators();
    //     coordinates.updateValueAndValidity();
    //     this.resetCoordinatesAndAddress();
    //   }
    // }
  }

  addGateDefinition(): void {
    this.gateDefinitionForms.push(new FormControl(null, [Validators.required]));
  }

  removeGateDefinition(index: number): void {
    this.gateDefinitionForms.removeAt(index);

    this.gateDefinitionForms.controls.forEach((c) => c.updateValueAndValidity());
  }

  getContacts(shippingType: ShippingType): Promise<User[]> {
    return new Promise((resolve) => {
      this.load(
        this.usersCommonApiService.getAllUsers(UserType.CUSTOMER, this.item?.id, shippingType),
        { isLoadingTransparent: true }
      )
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (users) => {
            resolve(users);
          },
          (err) => {
            resolve([]);
          }
        );
    });
  }

  setContactsControl(contacts) {
    this.contacts = contacts;
    if (contacts.length === 1) {
      this.form.controls.contacts.patchValue(contacts);
    } else {
      this.form.controls.contacts.setValue(null);
    }
  }

  handleAddNewContactEvent(event) {
    const formShippingType = this.form.value.shippingType;
    if (event.shippingType !== formShippingType) {
      this.form.controls.shippingType.setValue(event.shippingType);
      this.newContact = event.user;
    } else {
      /**TODO: detect the problem in app-dropdown to avoid using the below method and extra arrays */
      this.getContacts(this.form?.controls?.shippingType.value).then((users) => {
        this.contacts = users;
        const ids = [event.user.id, ...(this.form.controls.contacts.value || []).map((i) => i?.id)];
        const _contacts = [];
        ids.forEach((id) => {
          _contacts.push(this.contacts.find((c) => c.id === id));
        });
        this.form.controls.contacts.patchValue([..._contacts]);
      });
      this.newContact = null;
    }
  }

  resetCoordinatesAndAddress() {
    this.latitude = null;
    this.longitude = null;
    this.address = null;
  }

  private getAddress(latitude, longitude) {
    this.geoCoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
      if (status === 'OK') {
        if (results[0]) {
          this.zoom = 12;
          this.address = results[0].formatted_address;
          // this.city = results[0].address_components[results[0].address_components.length - 2].long_name;
        }
      }
    });
  }

  private onLatitudeValueChange(latitude) {
    this.latitude = latitude;
    this.getAddress(Number(this.latitude), Number(this.longitude));
    this.markerDragEnd(null, Number(this.latitude), Number(this.longitude));
    //this.setCityAccordingToSelectedLocation();
  }

  private onLongitudeValueChange(longitude) {
    this.longitude = longitude;
    this.getAddress(Number(this.latitude), Number(this.longitude));
    this.markerDragEnd(null, Number(this.latitude), Number(this.longitude));
    //this.setCityAccordingToSelectedLocation();
  }

  private setCityAccordingToSelectedLocation() {
    let polygonCoordinates = [];
    let polygon = [];
    let isSelectedLocationPartOfSupportedCities: boolean = false;
    isSelectedLocationPartOfSupportedCities = this.activeCities.some((city) => {
      polygonCoordinates = [];
      polygon = [];
      polygonCoordinates = city.polygonCoordinates?.split(/\n/);

      for (let i = 0; i < polygonCoordinates?.length; i++) {
        polygon.push({
          longitude: polygonCoordinates[i].split(',')[0],
          latitude: polygonCoordinates[i].split(',')[1],
        });
      }

      if (isPointInPolygon({ latitude: this.latitude, longitude: this.longitude }, polygon)) {
        this.city = city;
        this.form.controls['city'].setValue(city.name);
        return true;
      }
    });

    if (!isSelectedLocationPartOfSupportedCities) {
      this.form.controls['city'].setValue(null);
      this.form.controls['city'].markAsTouched();
    }
  }

  filterCities(options: any[]): any[] {
    Promise.resolve().then(() => this.changeDetector.markForCheck());
    return options;
  }

  private emitLocationValue(): void {
    const { name, city, defaultUser, contacts, ...formValue } = this.form.getRawValue();
    const defaultUserId = defaultUser?.id;
    const usersIds = (contacts || []).map((v) => v.id);
    const locationValue: LocationModel = {
      ...this.location,
      ...formValue,
      title: name,
      type: this.type,
      address: this.address,
      latitude: this.latitude,
      longitude: this.longitude,
      cityId: city?.id,
      defaultUserId,
      usersIds
    };
    this.valueChanges.emit(locationValue);
  }

}
